import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import PayrollDataGrid from "./PayrollDataGrid";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "./Payroll.css";

const { BonusPolicy, BonusGeneration } = config.REST_API;

/** Matches C# PayrollStructureLineAmountType */
const AmountType = { Fixed: 0, PercentOfGross: 1 };

function unwrapOne(res) {
  const body = res?.data;
  if (body == null || body.success === false) return { data: null, message: body?.message };
  if (body.data && typeof body.data === "object") return { data: body.data, message: null };
  return { data: null, message: null };
}

function normalizePolicy(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  const amountTypeNum = Number(get("amountType", "AmountType") ?? 0);
  return {
    id: String(get("id", "Id") ?? ""),
    name: String(get("name", "Name") ?? ""),
    amountType: amountTypeNum === AmountType.PercentOfGross ? AmountType.PercentOfGross : AmountType.Fixed,
    amountValue: Number(get("amountValue", "AmountValue") ?? 0),
    scopeSummary: String(get("scopeSummary", "ScopeSummary") ?? ""),
    minimumServiceMonths:
      get("minimumServiceMonths", "MinimumServiceMonths") != null && get("minimumServiceMonths", "MinimumServiceMonths") !== ""
        ? Number(get("minimumServiceMonths", "MinimumServiceMonths"))
        : null,
  };
}

function normalizeLine(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  return {
    userId: String(get("userId", "UserId") ?? ""),
    employeeName: String(get("employeeName", "EmployeeName") ?? ""),
    departmentName: String(get("departmentName", "DepartmentName") ?? ""),
    officeName: String(get("officeName", "OfficeName") ?? ""),
    grossSalary: Number(get("grossSalary", "GrossSalary") ?? 0),
    bonusAmount: Number(get("bonusAmount", "BonusAmount") ?? 0),
    hasSalaryAssignment: Boolean(get("hasSalaryAssignment", "HasSalaryAssignment")),
  };
}

function formatMoney(n) {
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
}

function formatPolicyAmount(policy) {
  if (!policy) return "—";
  if (policy.amountType === AmountType.PercentOfGross) return `${policy.amountValue}%`;
  return formatMoney(policy.amountValue);
}

function toDateInputValue(isoOrDate) {
  if (!isoOrDate) return "";
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

const BonusGeneratePage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { bonusPolicyId } = useParams();

  const [appliedDate, setAppliedDate] = useState(() => toDateInputValue(new Date()));
  const [policy, setPolicy] = useState(null);
  const [lines, setLines] = useState([]);
  const [totalBonus, setTotalBonus] = useState(0);
  const [alreadyGenerated, setAlreadyGenerated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadPrepare = useCallback(async () => {
    if (!bonusPolicyId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const qs = appliedDate ? `?appliedDate=${encodeURIComponent(appliedDate)}` : "";
      const res = await axiosBasic.get(`${BonusGeneration.PrepareBase}${encodeURIComponent(bonusPolicyId)}${qs}`);
      const { data, message } = unwrapOne(res);
      if (!data) {
        setPolicy(null);
        setLines([]);
        setLoadError(message || t("app.payroll.bonuses.generateLoadError"));
        return;
      }
      setPolicy(normalizePolicy(data.bonusPolicy ?? data.BonusPolicy));
      setLines((Array.isArray(data.lines) ? data.lines : Array.isArray(data.Lines) ? data.Lines : []).map(normalizeLine));
      setTotalBonus(Number(data.totalBonusAmount ?? data.TotalBonusAmount ?? 0));
      setAlreadyGenerated(Boolean(data.alreadyGenerated ?? data.AlreadyGenerated));
      const applied = data.appliedDate ?? data.AppliedDate;
      if (applied) setAppliedDate(toDateInputValue(applied));
    } catch (e) {
      setPolicy(null);
      setLines([]);
      setLoadError(e?.response?.data?.message || e?.message || t("app.payroll.bonuses.generateLoadError"));
    } finally {
      setLoading(false);
    }
  }, [bonusPolicyId, appliedDate, t]);

  useEffect(() => {
    loadPrepare();
  }, [loadPrepare]);

  const handleSave = async () => {
    if (!bonusPolicyId || !appliedDate || saving || alreadyGenerated) return;
    setSaving(true);
    try {
      const res = await axiosBasic.post(BonusGeneration.Post, {
        BonusPolicyId: bonusPolicyId,
        AppliedDate: appliedDate,
      });
      const body = res?.data;
      if (body && body.success) {
        Common.showSuccessMessage(t("app.payroll.bonuses.generateSaveSuccess"));
        history.push("/payroll/bonuses");
      } else {
        Common.showErrorMessage(body?.message || t("app.payroll.bonuses.generateSaveError"));
      }
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.bonuses.generateSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const policyColumns = useMemo(
    () => [
      { key: "name", label: t("app.payroll.bonuses.colName"), sortable: false },
      { key: "amountType", label: t("app.payroll.bonuses.colAmountType"), sortable: false },
      { key: "value", label: t("app.payroll.bonuses.colValue"), sortable: false },
      { key: "scope", label: t("app.payroll.bonuses.colScope"), sortable: false },
      { key: "minService", label: t("app.payroll.bonuses.colMinService"), sortable: false },
    ],
    [t]
  );

  const policyRows = useMemo(() => (policy ? [policy] : []), [policy]);

  const employeeColumns = useMemo(
    () => [
      { key: "employee", label: t("app.payroll.bonuses.generateColEmployee"), sortable: false },
      { key: "department", label: t("app.payroll.assignments.colDepartment"), sortable: false },
      { key: "office", label: t("app.payroll.assignments.colOffice"), sortable: false },
      { key: "gross", label: t("app.payroll.bonuses.generateColGross"), sortable: false },
      { key: "bonus", label: t("app.payroll.bonuses.generateColBonus"), sortable: false },
    ],
    [t]
  );

  return (
    <div className="documents-page payroll-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-stars" />}
        title={t("app.payroll.bonuses.generatePageTitle")}
        description={t("app.payroll.bonuses.generatePageSubtitle")}
        rightContent={
          <button type="button" className="btn btn-outline-secondary rounded-pill" onClick={() => history.push("/payroll/bonuses")}>
            {t("app.payroll.bonuses.generateBack")}
          </button>
        }
      />

      {loadError && (
        <div className="alert alert-warning mb-3" role="alert">
          {loadError}
        </div>
      )}

      <section className="mb-4" aria-labelledby="payroll-bn-gen-policy-heading">
        <h2 id="payroll-bn-gen-policy-heading" className="h6 fw-bold mb-3">
          {t("app.payroll.bonuses.generatePolicySection")}
        </h2>
        <PayrollDataGrid
          columns={policyColumns}
          rows={policyRows}
          getRowKey={(row) => row.id || "policy"}
          loading={loading && !policy}
          loadingMessage={t("app.payroll.bonuses.generateLoading")}
          emptyMessage={loadError ? "—" : t("app.payroll.bonuses.generatePolicyEmpty")}
          renderRow={(row) => (
            <>
              <td className="fw-semibold">{row.name || "—"}</td>
              <td>
                {row.amountType === AmountType.PercentOfGross
                  ? t("app.payroll.bonuses.typePercentGross")
                  : t("app.payroll.bonuses.typeFixed")}
              </td>
              <td>{formatPolicyAmount(row)}</td>
              <td>{row.scopeSummary || "—"}</td>
              <td>{row.minimumServiceMonths != null ? row.minimumServiceMonths : "—"}</td>
            </>
          )}
        />
      </section>

      <section className="payroll-overview-panel mb-4" aria-labelledby="payroll-bn-gen-date-heading">
        <h2 id="payroll-bn-gen-date-heading" className="h6 fw-bold mb-3">
          {t("app.payroll.bonuses.generateApplyDateSection")}
        </h2>
        <div className="row g-3">
          <div className="col-md-4 col-lg-3">
            <label className="att-modal-field-label" htmlFor="payroll-bn-gen-date">
              {t("app.payroll.bonuses.generateAppliedDate")}
            </label>
            <input
              id="payroll-bn-gen-date"
              type="date"
              className="form-control"
              value={appliedDate}
              onChange={(e) => setAppliedDate(e.target.value)}
              disabled={loading || saving}
            />
          </div>
        </div>
        {alreadyGenerated ? (
          <div className="alert alert-info mt-3 mb-0" role="status">
            {t("app.payroll.bonuses.generateAlreadyExists")}
          </div>
        ) : null}
      </section>

      <section className="mb-4" aria-labelledby="payroll-bn-gen-employees-heading">
        <h2 id="payroll-bn-gen-employees-heading" className="h6 fw-bold mb-3">
          {t("app.payroll.bonuses.generateEmployeesSection")}
        </h2>

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Loader active inline="centered">
              {t("app.payroll.bonuses.generateLoading")}
            </Loader>
          </div>
        ) : (
          <>
            <p className="documents-page-description ts-appr-results-hint mb-3">
              {t("app.payroll.bonuses.generatePreviewHint", {
                count: lines.length,
                total: formatMoney(totalBonus),
              })}
            </p>

            <PayrollDataGrid
              columns={employeeColumns}
              rows={lines}
              getRowKey={(row) => row.userId}
              emptyMessage={t("app.payroll.bonuses.generateNoEligible")}
              renderRow={(row) => (
                <>
                  <td className="fw-semibold">
                    {row.employeeName}
                    {!row.hasSalaryAssignment ? (
                      <span className="text-muted small d-block">{t("app.payroll.bonuses.generateNoSalary")}</span>
                    ) : null}
                  </td>
                  <td>{row.departmentName || "—"}</td>
                  <td>{row.officeName || "—"}</td>
                  <td>{formatMoney(row.grossSalary)}</td>
                  <td className="fw-semibold">{formatMoney(row.bonusAmount)}</td>
                </>
              )}
            />

            <div className="d-flex flex-wrap gap-2 justify-content-end mt-4">
              <button type="button" className="att-btn att-btn--ghost" onClick={() => history.push("/payroll/bonuses")} disabled={saving}>
                {t("app.payroll.common.cancel")}
              </button>
              <button
                type="button"
                className="att-btn att-btn--save-primary"
                onClick={handleSave}
                disabled={saving || alreadyGenerated || lines.length === 0 || !appliedDate}
              >
                {saving ? t("app.common.loading") : t("app.payroll.common.save")}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default BonusGeneratePage;
